import React, { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { Input } from "../../components/ui/input";
import { toast, Toaster } from "sonner";
import { Sparkles, Save, RotateCcw, Filter, ListChecks, Loader2 } from "lucide-react";

/**
 * Roadmap & feature toggles — every potential website enhancement, with a
 * switch to either turn it on (for built features) or capture the admin's
 * intent (for roadmap items). Saves dirty toggles in a single PUT.
 */
const STATUS_META = {
    "live": { label: "Live", className: "bg-emerald-100 text-emerald-800 border-emerald-300" },
    "shipped-always-on": { label: "Always on", className: "bg-sky-100 text-sky-800 border-sky-300" },
    "roadmap": { label: "Roadmap", className: "bg-amber-100 text-amber-800 border-amber-300" },
};

const CATEGORY_ORDER = ["Quick wins", "Revenue multipliers", "Differentiators", "Polish & ops"];
const CATEGORY_META = {
    "Quick wins": { tone: "sky", desc: "1–2 hours each. Highest ROI per hour spent." },
    "Revenue multipliers": { tone: "emerald", desc: "1 day each. Directly grows monthly revenue." },
    "Differentiators": { tone: "violet", desc: "2–3 days each. Sets you apart from competitors." },
    "Polish & ops": { tone: "stone", desc: "Quality-of-life improvements and operational tooling." },
};

export default function AdminFeatureFlags() {
    const [items, setItems] = useState([]);
    const [edits, setEdits] = useState({}); // key -> bool
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState("all"); // all · enabled · roadmap

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/feature-flags/");
            setItems(data.items || []);
            setEdits({});
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load flags");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    /** Current value for a flag — edit overlay if dirty, else server value. */
    const isOn = (it) => (edits[it.key] !== undefined ? edits[it.key] : it.enabled);

    const setOn = (it, value) => {
        setEdits((prev) => {
            const next = { ...prev };
            if (value === it.enabled) delete next[it.key];
            else next[it.key] = value;
            return next;
        });
    };

    const dirtyKeys = useMemo(() => Object.keys(edits), [edits]);

    const save = async () => {
        if (dirtyKeys.length === 0) return;
        setSaving(true);
        try {
            await api.put("/feature-flags/", {
                updates: dirtyKeys.map((k) => ({ key: k, enabled: edits[k] })),
            });
            toast.success(`Saved · ${dirtyKeys.length} change${dirtyKeys.length === 1 ? "" : "s"}`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const filteredItems = useMemo(() => {
        const lc = q.trim().toLowerCase();
        return items.filter((it) => {
            if (filter === "enabled" && !isOn(it)) return false;
            if (filter === "roadmap" && it.status !== "roadmap") return false;
            if (!lc) return true;
            return (
                it.title.toLowerCase().includes(lc) ||
                it.description.toLowerCase().includes(lc) ||
                it.category.toLowerCase().includes(lc)
            );
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, edits, q, filter]);

    const totals = useMemo(() => {
        const enabledCount = items.filter((it) => isOn(it)).length;
        const liveCount = items.filter((it) => it.status === "live" || it.status === "shipped-always-on").length;
        const roadmapWanted = items.filter((it) => it.status === "roadmap" && isOn(it)).length;
        return { total: items.length, enabledCount, liveCount, roadmapWanted };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, edits]);

    const grouped = useMemo(() => {
        const out = {};
        for (const c of CATEGORY_ORDER) out[c] = [];
        for (const it of filteredItems) (out[it.category] = out[it.category] || []).push(it);
        return out;
    }, [filteredItems]);

    return (
        <div data-testid="admin-feature-flags" className="space-y-6">
            <Toaster position="top-center" />

            <div className="flex justify-between items-end flex-wrap gap-3">
                <div>
                    <div className="label-eyebrow text-sky-700 flex items-center gap-1.5"><Sparkles className="w-3 h-3" />Roadmap</div>
                    <h1 className="font-serif text-4xl text-stone-900 mt-2">Features &amp; toggles</h1>
                    <p className="text-sm text-stone-600 mt-2 max-w-3xl">
                        Every potential enhancement to your website is listed below. For features that are already built, flipping the toggle controls visibility. For roadmap items, the toggle records your intent so the engineering team knows what to ship next.
                    </p>
                </div>
                <div className="grid grid-cols-3 gap-4 text-right">
                    <div><div className="font-serif text-3xl text-emerald-700">{totals.enabledCount}</div><div className="text-[10px] uppercase tracking-widest text-stone-500">Enabled</div></div>
                    <div><div className="font-serif text-3xl text-sky-700">{totals.liveCount}</div><div className="text-[10px] uppercase tracking-widest text-stone-500">Already live</div></div>
                    <div><div className="font-serif text-3xl text-amber-700">{totals.roadmapWanted}</div><div className="text-[10px] uppercase tracking-widest text-stone-500">Wanted (roadmap)</div></div>
                </div>
            </div>

            {/* Dirty banner — sticky save bar */}
            {dirtyKeys.length > 0 && (
                <div
                    className="sticky top-0 z-20 bg-emerald-700 text-white px-4 py-3 flex items-center justify-between flex-wrap gap-2 shadow-md"
                    data-testid="ff-dirty-banner"
                >
                    <div className="text-sm">
                        <strong className="font-serif text-lg">Pending changes</strong>
                        <span className="ml-3 text-emerald-100">{dirtyKeys.length} toggle{dirtyKeys.length === 1 ? "" : "s"} edited.</span>
                    </div>
                    <div className="flex gap-2">
                        <Button onClick={() => setEdits({})} variant="outline" className="rounded-none border-white text-emerald-800 bg-white hover:bg-emerald-50" data-testid="ff-reset">
                            <RotateCcw className="w-4 h-4 mr-2" />Discard
                        </Button>
                        <Button onClick={save} disabled={saving} className="rounded-none bg-white text-emerald-700 hover:bg-emerald-50 disabled:opacity-50" data-testid="ff-save">
                            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : <><Save className="w-4 h-4 mr-2" />Save {dirtyKeys.length} change{dirtyKeys.length === 1 ? "" : "s"}</>}
                        </Button>
                    </div>
                </div>
            )}

            {/* Search + filter strip */}
            <div className="bg-white border border-stone-200 p-3 flex flex-wrap gap-3 items-center">
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, description or category…" className="rounded-none border-stone-300 flex-1 min-w-[260px]" data-testid="ff-search" />
                <div className="flex gap-1 text-xs uppercase tracking-widest" data-testid="ff-filter-group">
                    {[
                        { id: "all", label: "All", icon: ListChecks },
                        { id: "enabled", label: "Enabled only", icon: Filter },
                        { id: "roadmap", label: "Roadmap only", icon: Sparkles },
                    ].map((f) => {
                        const active = filter === f.id;
                        return (
                            <button
                                key={f.id}
                                onClick={() => setFilter(f.id)}
                                className={`px-3 py-1 border ${active ? "border-sky-700 bg-sky-700 text-white" : "border-stone-300 text-stone-600 hover:border-sky-300"}`}
                                data-testid={`ff-filter-${f.id}`}
                            >
                                {f.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {loading ? (
                <div className="p-12 text-center text-stone-500">Loading…</div>
            ) : (
                CATEGORY_ORDER.map((cat) => {
                    const list = grouped[cat] || [];
                    if (list.length === 0) return null;
                    const meta = CATEGORY_META[cat];
                    return (
                        <section key={cat} data-testid={`ff-category-${cat.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                            <div className="mb-3">
                                <h2 className={`font-serif text-2xl text-stone-900`}>{cat}</h2>
                                <p className="text-xs text-stone-500">{meta?.desc}</p>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-3">
                                {list.map((it) => {
                                    const on = isOn(it);
                                    const dirty = edits[it.key] !== undefined;
                                    const sm = STATUS_META[it.status] || STATUS_META.roadmap;
                                    return (
                                        <label
                                            key={it.key}
                                            className={`block border p-4 cursor-pointer transition-colors ${on ? "border-emerald-500 bg-emerald-50/60" : "border-stone-200 bg-white hover:border-stone-400"} ${dirty ? "ring-2 ring-amber-400 ring-offset-1" : ""}`}
                                            data-testid={`ff-card-${it.key}`}
                                        >
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className="font-medium text-stone-900">{it.title}</span>
                                                        <span className={`text-[9px] uppercase tracking-widest border px-1.5 py-0.5 ${sm.className}`} data-testid={`ff-status-${it.key}`}>
                                                            {sm.label}
                                                        </span>
                                                    </div>
                                                    <p className="text-xs text-stone-600 mt-1 leading-relaxed">{it.description}</p>
                                                    {it.impact && <div className="text-[10px] uppercase tracking-widest text-stone-500 mt-2">{it.impact}</div>}
                                                </div>
                                                <Switch
                                                    checked={on}
                                                    onCheckedChange={(v) => setOn(it, v)}
                                                    disabled={it.status === "shipped-always-on"}
                                                    data-testid={`ff-toggle-${it.key}`}
                                                />
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>
                        </section>
                    );
                })
            )}

            <p className="text-[11px] text-stone-400 italic">
                Tip: switch a roadmap item ON to vote for it — it doesn&apos;t build itself, but it signals what to prioritise next. Built features (shown as <strong>Live</strong> or <strong>Always on</strong>) honour the toggle immediately after save.
            </p>
        </div>
    );
}
